// src/pages/Commissions/CommissionArticlesPage.jsx
import React, { useState, useEffect, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  getCommissionArticles,
  deleteArticle,
  toggleArticleState,
} from "../../services/articlescommissionsServices";
import { getGroupNameById } from "../../services/groupServices";
import { useUser } from "../../contexts/UserContext";
import { getRoleFromRoleId } from "./../../utils/roleHelper";
import { toast } from "react-toastify";
import Slider from "react-slick";
import Button from "../../components/Button/Button";
import ButtonEdit from "../../components/Button/ButtonEdit";
import ButtonDelete from "../../components/Button/ButtonDelete";
import CreateArticleModal from "./CreateArticleModal";
import EditArticleModal from "./EditArticleModal";
import useScrollToTop from "./../../hooks/useScrollToTop";
import ScrollIndicator from "./../../hooks/ScrollIndicator";
import { FaSearch } from "react-icons/fa";

import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";

const normalize = (s) =>
  (s || "")
    .toString()
    .normalize("NFD")
    .replace(/\p{Diacritic}/gu, "")
    .toLowerCase()
    .trim();

const filterArticlesByRole = (data, role, groupId) => {
  const gid = parseInt(groupId, 10);

  return data.filter((article) => {
    if (article.group_id !== gid) return false;

    // Si article non actif, on le cache pour Officiels / Responsables_clubs / Utilisateurs
    if (!article.is_active) {
      return !["Officiels", "Responsables_clubs", "Utilisateurs"].includes(
        role
      );
    }
    return true;
  });
};

const CommissionArticlesPage = () => {
  useScrollToTop();
  const { id } = useParams();
  const { user } = useUser();
  const navigate = useNavigate();
  const userRole = getRoleFromRoleId(user?.role_id);

  const [articles, setArticles] = useState([]);
  const [commissionName, setCommissionName] = useState("");
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [currentArticle, setCurrentArticle] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");

  useEffect(() => {
    const fetchArticles = async () => {
      try {
        const data = await getCommissionArticles(id);
        const filtered = filterArticlesByRole(data, userRole, id);
        // Tri décroissant par date d’article
        filtered.sort((a, b) => {
          const da = new Date(a.date_article || a.created_at || 0);
          const db = new Date(b.date_article || b.created_at || 0);
          return db - da;
        });
        setArticles(filtered);
      } catch (error) {
        console.error(error);
        toast.error("Erreur lors de la récupération des articles");
      }
    };

    const fetchCommissionName = async () => {
      try {
        const commission = await getGroupNameById(id);
        setCommissionName(commission.nom);
      } catch (error) {
        console.error(error);
        toast.error(
          "Erreur lors de la récupération des informations de la commission"
        );
      }
    };

    fetchArticles();
    fetchCommissionName();
  }, [id, userRole]);

  const handleOpenModal = () => setIsModalOpen(true);
  const handleCloseModal = () => setIsModalOpen(false);

  const handleOpenEditModal = (article) => {
    setCurrentArticle(article);
    setIsEditModalOpen(true);
  };

  const handleCloseEditModal = () => {
    setIsEditModalOpen(false);
    setCurrentArticle(null);
  };

  const handleRefreshList = async () => {
    try {
      const data = await getCommissionArticles(id);
      const filtered = filterArticlesByRole(data, userRole, id);
      filtered.sort((a, b) => {
        const da = new Date(a.date_article || a.created_at || 0);
        const db = new Date(b.date_article || b.created_at || 0);
        return db - da;
      });
      setArticles(filtered);
    } catch (error) {
      console.error(error);
      toast.error("Erreur lors de la récupération des articles");
    }
  };

  const handleDeleteArticle = async (articleId) => {
    if (window.confirm("Êtes-vous sûr de vouloir supprimer cet article ?")) {
      try {
        await deleteArticle(articleId);
        toast.success("Article supprimé avec succès.");
        handleRefreshList();
      } catch (error) {
        console.error(error);
        toast.error("Erreur lors de la suppression de l'article");
      }
    }
  };

  const handleToggleArticle = async (article) => {
    try {
      await toggleArticleState(article.id, !article.is_active);
      toast.success(
        `L'article a été ${
          article.is_active ? "désactivé" : "activé"
        } avec succès.`
      );
      handleRefreshList();
    } catch (error) {
      console.error(error);
      toast.error("Erreur lors de la mise à jour de l'état de l'article.");
    }
  };

  const settings = {
    dots: true,
    infinite: true,
    speed: 500,
    slidesToShow: 1,
    slidesToScroll: 1,
    autoplay: true,
    autoplaySpeed: 3000,
    arrows: false,
  };

  // Vérifier si l'utilisateur a le droit d'éditer ou de supprimer un article
  const canEditOrDelete = (article) => {
    return (
      (user?.id === article.user_id ||
        ["Administrateur", "Bureau", "Membres", "Membres_Arbitres"].includes(
          userRole
        )) &&
      !["Officiels", "Responsables_clubs", "Utilisateurs"].includes(userRole)
    );
  };

  const canSeeTag = () => {
    return !["Officiels", "Responsables_clubs", "Utilisateurs"].includes(
      userRole
    );
  };

  const visibleArticles = useMemo(() => {
    const term = normalize(searchTerm);
    if (!term) return articles;
    return articles.filter((a) =>
      normalize(a.titre).includes(term)
    );
  }, [articles, searchTerm]);

  const formatDate = (d) => {
    if (!d) return "";
    const date = new Date(d);
    if (Number.isNaN(date.getTime())) return "";
    return date.toLocaleDateString("fr-FR", {
      day: "2-digit",
      month: "long",
      year: "numeric",
    });
  };

  return (
    <div className="min-h-screen flex flex-col w-full mx-auto pt-[130px] sm:pt-[130px] md:pt-[150px] lg:pt-[150px] xl:pt-[150px] pb-10 bg-slate-50 dark:bg-slate-900 dark:text-white">
      <ScrollIndicator />

      {/* Header */}
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-6 md:mb-8">
          <p className="text-[11px] font-semibold tracking-[0.35em] uppercase text-primary mb-1">
            Articles Commissions
          </p>
          <h1 className="text-2xl sm:text-3xl md:text-4xl font-extrabold tracking-tight font-montserrat uppercase">
            {commissionName || "Commission"}
          </h1>
          <p className="mt-3 text-sm md:text-base text-slate-600 dark:text-slate-300 max-w-2xl mx-auto">
            Retrouvez ici les articles, comptes-rendus et informations publiés
            par la commission tout au long de la saison.
          </p>
        </div>

        {/* Barre d'actions */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 mb-4">
          <div className="flex gap-2 justify-center md:justify-start">
            {user &&
              ["Administrateur", "Bureau", "Membres", "Membres_Arbitres"].includes(
                userRole
              ) && (
                <Button
                  text="Créer un Article"
                  onClick={handleOpenModal}
                  className="gap-2"
                />
              )}
            <Button text="Retour" onClick={() => navigate(-1)} />
          </div>

          {/* Recherche + compteur */}
          <div className="w-full md:w-1/2 flex flex-col gap-2">
            <div className="relative">
              <FaSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-xs" />
              <input
                type="text"
                placeholder="Rechercher un article par titre..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-8 pr-3 py-2 rounded-lg border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800 text-xs sm:text-sm focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary"
              />
            </div>
            <p className="text-[11px] text-right text-slate-500 dark:text-slate-300">
              {visibleArticles.length} article
              {visibleArticles.length > 1 && "s"} affiché
              {visibleArticles.length > 1 && "s"}.
            </p>
          </div>
        </div>
      </div>

      {/* Modales */}
      <CreateArticleModal
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        refreshList={handleRefreshList}
        groupId={id}
      />
      <EditArticleModal
        isOpen={isEditModalOpen}
        onClose={handleCloseEditModal}
        refreshList={handleRefreshList}
        article={currentArticle}
      />

      {/* Liste des articles */}
      <div className="w-full max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 mt-2">
        {visibleArticles.length > 0 ? (
          <div className="space-y-6">
            {visibleArticles.map((article) => {
              const images = [
                article.photo_url1,
                article.photo_url2,
                article.photo_url3,
              ].filter(Boolean);
              const hasImages = images.length > 0;

              return (
                <article
                  key={article.id}
                  className="relative flex flex-col md:flex-row rounded-2xl bg-white/95 dark:bg-slate-900/95 border border-slate-200/80 dark:border-slate-700/80 shadow-lg overflow-hidden transition-all duration-200 hover:shadow-xl hover:-translate-y-0.5"
                >
                  {/* Badge d'état */}
                  {canSeeTag() && (
                    <div className="absolute top-3 right-3 z-10">
                      <span
                        className={`inline-flex items-center px-3 py-1 rounded-full text-[11px] font-semibold ${
                          article.is_active
                            ? "bg-emerald-500 text-white"
                            : "bg-amber-500 text-white"
                        }`}
                      >
                        {article.is_active ? "Diffusée" : "Non diffusée"}
                      </span>
                    </div>
                  )}

                  {/* Images */}
                  <div className="md:w-1/2 w-full bg-slate-900/90">
                    {hasImages ? (
                      images.length > 1 ? (
                        <Slider {...settings}>
                          {images.map((image, index) => (
                            <div
                              key={index}
                              className="w-full h-64 sm:h-72 md:h-full"
                            >
                              <img
                                src={image}
                                alt={`Visuel ${index + 1}`}
                                className="w-full h-full object-cover"
                              />
                            </div>
                          ))}
                        </Slider>
                      ) : (
                        <div className="w-full h-64 sm:h-72 md:h-full">
                          <img
                            src={images[0]}
                            alt="Visuel article"
                            className="w-full h-full object-cover"
                          />
                        </div>
                      )
                    ) : (
                      <div className="w-full h-64 sm:h-72 md:h-full bg-slate-800 flex items-center justify-center">
                        <img
                          src="/img/default_image.jpg"
                          alt="Default article cover"
                          className="w-full h-full object-cover opacity-80"
                        />
                      </div>
                    )}
                  </div>

                  {/* Contenu */}
                  <div className="md:w-1/2 w-full p-4 sm:p-5 flex flex-col justify-between">
                    <div>
                      <h3 className="text-lg sm:text-xl font-bold text-slate-800 dark:text-white mb-2 font-montserrat">
                        {article.titre}
                      </h3>

                      <div
                        className="text-sm text-slate-700 dark:text-slate-200 mb-3 sm:mb-4 prose prose-sm max-w-none dark:prose-invert"
                        dangerouslySetInnerHTML={{
                          __html: article.contenu,
                        }}
                      />

                      <p className="text-[11px] sm:text-xs text-slate-500 dark:text-slate-300">
                        Publié le {formatDate(article.date_article)} par la{" "}
                        {commissionName}
                      </p>

                      {!article.is_active && user?.id === article.user_id && (
                        <p className="mt-2 text-xs text-amber-600 dark:text-amber-400 font-semibold">
                          Cet article est en attente de validation par le
                          Bureau. Une notification a été envoyée.
                        </p>
                      )}
                    </div>

                    {/* Boutons actions */}
                    {canEditOrDelete(article) && (
                      <div className="flex flex-wrap gap-2 mt-4">
                        <ButtonEdit
                          onClick={() => handleOpenEditModal(article)}
                        />
                        <ButtonDelete
                          onClick={() => handleDeleteArticle(article.id)}
                        />
                        {["Administrateur", "Bureau"].includes(userRole) && (
                          <Button
                            text={
                              article.is_active ? "Désactiver" : "Activer"
                            }
                            onClick={() => handleToggleArticle(article)}
                            className="text-sm"
                          />
                        )}
                      </div>
                    )}
                  </div>
                </article>
              );
            })}
          </div>
        ) : (
          <div className="mt-10 text-center text-sm text-slate-500 dark:text-slate-300">
            Aucun article trouvé pour cette commission.
          </div>
        )}
      </div>
    </div>
  );
};

export default CommissionArticlesPage;
